import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Paper,
  Chip,
  Button,
  Stack,
  TextField,
  MenuItem,
  Divider,
  Grid,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Close,
  Email,
  Person,
  CalendarToday,
  Warning,
  CheckCircle,
  HourglassEmpty,
  Block,
  Chat,
  LocationOn,
  Edit,
} from "@mui/icons-material";
import FeedbackAuditLogs from "./feedback/FeedbackAuditLogs";
import FeedbackActionModal from "./feedback/FeedbackActionModal";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const FeedbackDetailModal = ({
  open,
  onClose,
  feedback,
  onStatusUpdate,
  chitalishte = null,
  onSuccess,
}) => {
  const [selectedStatus, setSelectedStatus] = useState(feedback.status);
  const [adminNotes, setAdminNotes] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [actionModalOpen, setActionModalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedStatus(feedback.status);
      setAdminNotes("");
    }
  }, [open, feedback]);

  const handleStatusUpdate = async () => {
    if (selectedStatus !== feedback.status) {
      if (selectedStatus === "REJECTED" && !adminNotes.trim()) {
        alert("Моля, добавете бележка при отхвърляне на сигнала");
        return;
      }

      setIsUpdating(true);

      // Запазете старата стойност за rollback
      const oldStatus = feedback.status;
      const oldNotes = adminNotes;

      try {
        // Оптимистично обновяване - покажете новата стойност веднага
        const updatedFeedback = {
          ...feedback,
          status: selectedStatus,
          adminNotes: adminNotes,
          updatedAt: new Date().toISOString(),
        };

        // Ако имате setFeedback функция, извикайте я тук
        // setFeedback(updatedFeedback);

        // Изпратете промяната към сървъра
        await onStatusUpdate(feedback.id, selectedStatus, adminNotes);
      } catch (error) {
        // Rollback при грешка
        setSelectedStatus(oldStatus);
        setAdminNotes(oldNotes);
        console.error("Грешка при обновяване на статус:", error);
        // Можете да покажете съобщение за грешка
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const getStatusConfig = (status) => {
    const statusConfigs = {
      NEW: {
        color: "warning",
        icon: <Warning />,
        label: "Нов",
        description: "Нов сигнал, чака обработка",
      },
      IN_REVIEW: {
        color: "info",
        icon: <HourglassEmpty />,
        label: "В преглед",
        description: "Сигналът се разглежда",
      },
      PROCESSED: {
        color: "success",
        icon: <CheckCircle />,
        label: "Обработен",
        description: "Сигналът е обработен успешно",
      },
      REJECTED: {
        color: "error",
        icon: <Block />,
        label: "Отхвърлен",
        description: "Сигналът е отхвърлен",
      },
    };
    return statusConfigs[status] || statusConfigs.NEW;
  };

  const getFeedbackTypeLabel = (type) => {
    const typeLabels = {
      location: "Местоположение",
      basic_info: "Основни данни",
      activity: "Дейност",
      website: "Уебсайт",
      other: "Друго",
    };
    return typeLabels[type] || type;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("bg-BG"),
      time: date.toLocaleTimeString("bg-BG"),
    };
  };

  const createdDate = formatDate(feedback.createdAt);
  const updatedDate = feedback.updatedAt
    ? formatDate(feedback.updatedAt)
    : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Детайли за сигнала</Typography>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            sx={{ mb: 2 }}
          >
            <Tab label="Информация" />
            <Tab label="История на промените" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              {/* Основна информация */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="subtitle1" fontWeight="600">
                      Основна информация
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {isUpdating && (
                        <Typography variant="caption" color="text.secondary">
                          Обновяване...
                        </Typography>
                      )}
                      <Chip
                        icon={getStatusConfig(selectedStatus).icon}
                        label={getStatusConfig(selectedStatus).label}
                        color={getStatusConfig(selectedStatus).color}
                        variant="filled"
                        sx={{
                          opacity: isUpdating ? 0.7 : 1,
                          transition: "all 0.3s ease",
                        }}
                      />
                    </Stack>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Person
                          sx={{ mr: 1, color: "text.secondary", fontSize: 20 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Изпратено от:
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        {feedback.senderName || "Анонимен"}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Email
                          sx={{ mr: 1, color: "text.secondary", fontSize: 20 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Email:
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        {feedback.email || "Не е посочен"}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Chat
                          sx={{ mr: 1, color: "text.secondary", fontSize: 20 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Тип обратна връзка:
                        </Typography>
                      </Box>
                      <Chip
                        label={getFeedbackTypeLabel(feedback.feedbackType)}
                        size="small"
                        variant="outlined"
                      />
                    </Grid>

                    {feedback.chitalishteName && (
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <LocationOn
                            sx={{
                              mr: 1,
                              color: "text.secondary",
                              fontSize: 20,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Читалище:
                          </Typography>
                        </Box>
                        <Typography variant="body1">
                          {feedback.chitalishteName}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {/* Съобщение */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
                  <Typography variant="subtitle1" fontWeight="600" mb={2}>
                    Съобщение
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "white",
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      variant="body1"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {feedback.message}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Мета информация */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
                  <Typography variant="subtitle1" fontWeight="600" mb={2}>
                    Мета информация
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Създаден на:
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 0.5 }}
                      >
                        <CalendarToday
                          sx={{ mr: 1, fontSize: 16, color: "text.secondary" }}
                        />
                        <Typography variant="body1">
                          {createdDate.date} {createdDate.time}
                        </Typography>
                      </Box>
                    </Grid>

                    {updatedDate && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Последно обновен на:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mt: 0.5,
                          }}
                        >
                          <CalendarToday
                            sx={{
                              mr: 1,
                              fontSize: 16,
                              color: "text.secondary",
                            }}
                          />
                          <Typography variant="body1">
                            {updatedDate.date} {updatedDate.time}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {/* Административни бележки */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
                  <Typography variant="subtitle1" fontWeight="600" mb={2}>
                    Административни бележки
                  </Typography>
                  {feedback.notes && (
                    <Box sx={{ mb: 2, p: 2, border: '1px dashed grey', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">Текущи бележки:</Typography>
                      <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                        {feedback.notes}
                      </Typography>
                    </Box>
                  )}
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Добавете нова бележка..."
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    variant="outlined"
                  />
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <FeedbackAuditLogs feedbackId={feedback.id} />
          </TabPanel>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
            width="100%"
          >
            {/* <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => setActionModalOpen(true)}
            >
              Обработи сигнал
            </Button> */}

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Статус:
              </Typography>
              <TextField
                select
                size="small"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="NEW">Нов</MenuItem>
                <MenuItem value="IN_REVIEW">В преглед</MenuItem>
                <MenuItem value="PROCESSED">Обработен</MenuItem>
                <MenuItem value="REJECTED">Отхвърлен</MenuItem>
              </TextField>
              <Button
                variant="contained"
                onClick={handleStatusUpdate}
                disabled={selectedStatus === feedback.status || isUpdating}
                size="small"
              >
                {isUpdating ? "Обновяване..." : "Обнови статус"}
              </Button>
            </Stack>

            <Button onClick={onClose} variant="outlined">
              Затвори
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <FeedbackActionModal
        open={actionModalOpen}
        onClose={() => setActionModalOpen(false)}
        feedback={feedback}
        chitalishte={chitalishte}
        onSuccess={() => {
          setActionModalOpen(false);
          onClose();
        }}
      />
    </>
  );
};

export default FeedbackDetailModal;
